import gift from '@/assets/webp/gift.webp';
import { CONSTANT } from '@/store/constants';
import { useAddress } from '@/store/selector';
import { ErrorMessage, mergeAPIError } from '@/utils/errors';
import { waitForTransactionReceipt } from '@/utils/wait-transaction';
import { MiniKit } from '@worldcoin/minikit-js';
import { motion } from 'motion/react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { toastApp } from '../toast-message/sonner';

export const aniProps = {
    initial: { '--x': '100%', scale: 0.8, '--primary': 'white' },
    animate: { '--x': '-100%', scale: 1, '--primary': 'white' },
    whileTap: { scale: 0.95 },
    transition: {
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: 1,
        type: 'spring',
        stiffness: 20,
        damping: 15,
        mass: 2,
        scale: {
            type: 'spring',
            stiffness: 200,
            damping: 5,
            mass: 0.5,
        },
    },
} as any;

export function ClaimFreeWld() {
    const address = useAddress();
    const { t } = useTranslation();
    const { data, isLoading } = useCheckClaim(address);
    const { loading, claim } = useClaim();

    const handleClaim = useCallback(async () => {
        if (!data || !data.signature || data.status !== 'CLAIMABLE') {
            toastApp.error(t('You are not eligble for reward'));
            return;
        }

        await claim();
    }, [claim, data, t]);

    if (isLoading) {
        return null;
    }

    if (data?.status === 'CLAIMED') {
        return (
            <div
                className=" rounded-full p-[1px] cursor-pointer relative"
                style={{
                    background:
                        'linear-gradient(90deg, #FF975B 0%, #E6FF8E 50%, #5FFFB4 100%)',
                }}
            >
                <div
                    className="bg-[#1C1C1C] rounded-full px-[7px] py-[3px] flex items-center gap-1 "
                    style={{
                        maskImage:
                            'linear-gradient(-75deg,var(--primary) calc(var(--x) + 20%),transparent calc(var(--x) + 30%),var(--primary) calc(var(--x) + 100%))',
                    }}
                >
                    <img src={gift} className="w-4 h-4" />
                    <p className="text-white font-[450] text-sm leading-5 truncate">
                        {t('Claimed')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <motion.button
            {...aniProps}
            disabled={loading}
            className=" rounded-full p-[1px] cursor-pointer relative  overflow-hidden"
            style={{
                background:
                    'linear-gradient(90deg, #FF975B 0%, #E6FF8E 50%, #5FFFB4 100%)',
            }}
            onClick={() => {
                if (!data) {
                    toastApp.error(t('You are not eligble for reward'));
                    return;
                }
                if (data.status === 'CLAIMED' || loading) {
                    return;
                }

                handleClaim();
            }}
        >
            <div
                className="bg-[#1C1C1C] rounded-full px-[7px] py-[3px] flex items-center gap-1 w-full"
                style={{
                    maskImage:
                        'linear-gradient(-75deg,var(--primary) calc(var(--x) + 20%),transparent calc(var(--x) + 30%),var(--primary) calc(var(--x) + 100%))',
                }}
            >
                {loading ? (
                    <>
                        <Spinner />
                        <p className="text-white font-[450] text-sm leading-5 truncate">
                            {t('Claiming')}
                        </p>
                    </>
                ) : (
                    <>
                        <img src={gift} className="w-4 h-4" />
                        <p className="text-white font-[450] text-sm leading-5 truncate">
                            {t('Claim Free WLD')}
                        </p>
                    </>
                )}
            </div>
            <span
                style={{
                    mask: 'linear-gradient(rgb(0,0,0), rgb(0,0,0)) content-box exclude,linear-gradient(rgb(0,0,0), rgb(0,0,0))',
                    WebkitMask:
                        'linear-gradient(rgb(0,0,0), rgb(0,0,0)) content-box exclude,linear-gradient(rgb(0,0,0), rgb(0,0,0))',
                    backgroundImage:
                        'linear-gradient(-75deg,var(--primary)/10% calc(var(--x)+20%),var(--primary)/50% calc(var(--x)+25%),var(--primary)/10% calc(var(--x)+100%))',
                }}
                className="absolute inset-0 z-10 block rounded-[inherit] p-px"
            />
        </motion.button>
    );
}

function useClaim() {
    const address = useAddress();
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const refTimeout = useRef<NodeJS.Timeout | null>(null);
    const { mutate } = useCheckClaim(address);
    const claim = useCallback(async () => {
        try {
            if (!MiniKit.isInstalled()) {
                throw new Error('Minikit not install');
            }

            setLoading(true);

            const data = await fetchData(
                `${CONSTANT.API_EARN_URL}/api/airdrop/claim-signature?wallet=${address}&campaign=FUTURE_AIRDROP`
            );
            if (!data || !data.signature) {
                throw new Error('You are not eligble for reward');
            }

            refTimeout.current = setTimeout(() => {
                setLoading(false);
            }, 15_000);

            const payload = {
                transaction: [
                    {
                        address: CONSTANT.CONTRACT_CLAIM_FREE_WLD,
                        abi: [
                            {
                                type: 'function',
                                name: 'claim',
                                inputs: [
                                    {
                                        name: '_target',
                                        type: 'address',
                                        internalType: 'address',
                                    },
                                    {
                                        name: '_callData',
                                        type: 'bytes',
                                        internalType: 'bytes',
                                    },
                                    {
                                        name: '_nonce',
                                        type: 'bytes32',
                                        internalType: 'bytes32',
                                    },
                                    {
                                        name: '_signature',
                                        type: 'bytes',
                                        internalType: 'bytes',
                                    },
                                ],
                                outputs: [],
                                stateMutability: 'nonpayable',
                            },
                        ],
                        functionName: 'claim',
                        args: [
                            data.token,
                            data.callData,
                            data.nonce,
                            data.signature,
                        ],
                    },
                ],
            };
            console.log('payload:', payload);

            const { finalPayload } =
                await MiniKit.commandsAsync.sendTransaction(payload);
            console.log('finalPayload:', finalPayload);

            if (refTimeout.current) {
                clearTimeout(refTimeout.current);
                refTimeout.current = null;
            }

            if (finalPayload.status === 'error') {
                const code = finalPayload.error_code as string;
                let msg = null;
                if (code === 'invalid_request') {
                    msg = 'You are not eligible for reward';
                } else {
                    msg = ErrorMessage(finalPayload.error_code);
                }

                throw new Error(
                    msg || 'Something went wrong, please try again'
                );
            }
            const txHash = await waitForTransactionReceipt(
                finalPayload.transaction_id
            );
            await verifyClaim(txHash);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await mutate();
            toastApp.success(t('Claim successfully'));
        } catch (error: any) {
            const msg =
                error?.message || 'Something went wrong, please try again';
            toastApp.error(t(msg));
        } finally {
            setLoading(false);
        }
    }, [address, mutate, t]);

    return { claim, loading };
}

async function fetchData(url: string) {
    const response = await fetch(url);
    if (!response.ok) {
        const msg = await response
            .json()
            .then((e: any) => mergeAPIError(e))
            .catch((e: any) => e.message ?? response.statusText);
        throw new Error(`Failed with status ${response.status} :${msg}`);
    }
    const data = (await response.json()) as {
        signature: string | null;
        amount: string | null;
        nonce: string | null; //'CLAIMABLE' | 'CLAIMED';
        status: string | null;
        campaign: string | null;
        callData: string | null;
        token: string | null;
    };

    return data;
}

async function verifyClaim(txHash: string) {
    try {
        const response = await fetch(
            `${CONSTANT.API_EARN_URL}/api/airdrop/verify-claim?txHash=${txHash}`
        );

        const data = await response.json();
        return { data };
    } catch (error) {
        console.log(error);
        return null;
    }
}

function useCheckClaim(address: string) {
    const response = useSWR(
        `${CONSTANT.API_EARN_URL}/api/airdrop/claim-signature?wallet=${address}&campaign=FUTURE_AIRDROP`,
        fetchData,
        { dedupingInterval: 1000 }
    );
    return response;
}

export function Spinner() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            className="animate-spin"
        >
            <g clipPath="url(#clip0_957_3780)">
                <path
                    d="M7.25798 1.37416C7.43373 1.35472 7.61001 1.40588 7.74804 1.5164C7.88607 1.62692 7.97454 1.78775 7.99398 1.9635C8.01343 2.13925 7.96227 2.31553 7.85175 2.45355C7.74123 2.59158 7.5804 2.68005 7.40465 2.6995C6.05241 2.8544 4.81054 3.52037 3.93332 4.56105C3.05609 5.60174 2.60988 6.9384 2.68605 8.29735C2.76222 9.65629 3.35501 10.9347 4.34303 11.8709C5.33106 12.807 6.63957 13.33 8.00065 13.3328V14.6662C4.31865 14.6662 1.33398 11.6822 1.33398 7.9995C1.33398 4.5955 3.89932 1.74683 7.25798 1.37416Z"
                    fill="url(#paint0_linear_957_3780)"
                />
                <path
                    d="M10.8813 2.69335C10.9825 2.54839 11.1371 2.44955 11.3111 2.41854C11.4851 2.38754 11.6643 2.42691 11.8093 2.52801C12.6919 3.14171 13.4128 3.95998 13.9104 4.91289C14.408 5.8658 14.6675 6.92501 14.6667 8.00001C14.6667 11.682 11.682 14.6667 8 14.6667V13.3333C9.13568 13.3335 10.2418 12.9711 11.1572 12.299C12.0726 11.6269 12.7496 10.6801 13.0895 9.59648C13.4295 8.51288 13.4147 7.34905 13.0472 6.27445C12.6798 5.19986 11.9789 4.27061 11.0467 3.62201C10.9748 3.57192 10.9135 3.50815 10.8663 3.43436C10.8191 3.36057 10.7869 3.27819 10.7715 3.19194C10.7562 3.1057 10.758 3.01727 10.7768 2.93172C10.7957 2.84617 10.8312 2.76516 10.8813 2.69335Z"
                    fill="url(#paint1_linear_957_3780)"
                />
            </g>
            <defs>
                <linearGradient
                    id="paint0_linear_957_3780"
                    x1="8.00002"
                    y1="0.999995"
                    x2={8}
                    y2={15}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" />
                    <stop offset={1} stopColor="white" stopOpacity="0.55" />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_957_3780"
                    x1={11}
                    y1={2}
                    x2={11}
                    y2={15}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" stopOpacity={0} />
                    <stop offset={1} stopColor="white" stopOpacity="0.55" />
                </linearGradient>
                <clipPath id="clip0_957_3780">
                    <rect width={16} height={16} fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
}
